import React, { useMemo, useState } from 'react';
import { TrackerCatalyst, TrackerCatalystOutcome } from '../../types';
import { catalystTrackerApi } from '../../services/catalystTrackerApi';
import TrackerModal from './TrackerModal';
import { CATALYST_TYPES, MODALITIES, OUTCOMES, addDays, normHist } from './helpers';

interface Props {
  target: TrackerCatalyst | null; // null = suggesting a brand-new catalyst
  rows: TrackerCatalyst[]; // for the Company/Asset/Mechanism/Tumor-type datalist hints
  onClose: () => void;
  onSubmitted: () => void;
}

type FormState = Record<string, string>;

// Field list and copy mirror catalyst_tracker/templates/catalyst_tracker/index.html's #editDlg form exactly.
const SuggestCatalystModal: React.FC<Props> = ({ target, rows, onClose, onSubmitted }) => {
  const isCorrection = !!target;
  const uniqueValues = (key: 'company' | 'asset' | 'moa' | 'histology') => {
    const set = new Set<string>();
    rows.forEach((r) => { const v = (r as Record<string, unknown>)[key]; if (typeof v === 'string' && v) set.add(v); });
    return Array.from(set).sort();
  };
  const companyOptions = useMemo(() => uniqueValues('company'), [rows]);
  const assetOptions = useMemo(() => uniqueValues('asset'), [rows]);
  const moaOptions = useMemo(() => uniqueValues('moa'), [rows]);
  // Normalized so the datalist nudges new/corrected entries toward the canonical spelling (helpers.ts's
  // normHist) instead of adding yet another variant ("Breast cancer", "Ovarian cancer", ...).
  const histologyOptions = useMemo(() => [...new Set(uniqueValues('histology').map(normHist))].sort(), [rows]);
  const [form, setForm] = useState<FormState>(() => {
    if (!target) return {};
    const keys = [
      'company', 'ticker', 'asset', 'generic', 'target', 'moa', 'modality', 'phase', 'trial', 'nct',
      'indication', 'histology', 'area', 'type', 'event', 'date', 'window', 'status', 'outcome', 'data',
      'venue', 'source', 'original_window', 'delay_note',
    ];
    const init: FormState = {};
    for (const k of keys) {
      const v = (target as Record<string, unknown>)[k];
      if (typeof v === 'string') init[k] = v;
    }
    return init;
  });
  const [reason, setReason] = useState('');
  const [delayed, setDelayed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const update = (key: string, value: string) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === 'date' && target?.date && value && value > addDays(target.date, 14) && !next.original_window) {
        setDelayed(true);
        next.original_window = target.window || target.date;
      }
      return next;
    });
  };

  const sourceValid = useMemo(() => !!form.source && /^https?:\/\//i.test(form.source), [form.source]);
  const note = !form.source ? 'Source URL is required.' : !sourceValid ? 'Source must start with http:// or https://' : '';

  const handleSubmit = async () => {
    setError(null);
    if (!sourceValid) return;
    if (!form.event) { setError('Event is required.'); return; }
    if (!isCorrection && !(form.company && form.asset)) { setError('Company and asset are required.'); return; }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = { kind: isCorrection ? 'correction' : 'new', reason: reason || undefined, delayed: delayed || undefined };
      if (isCorrection) payload.target_id = target!.id;
      for (const [k, v] of Object.entries(form)) if (v) payload[k] = k === 'outcome' ? (v as TrackerCatalystOutcome) : v;
      await catalystTrackerApi.submitSuggestion(payload);
      setDone(true);
      setTimeout(() => { onSubmitted(); onClose(); }, 900);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not submit the suggestion. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TrackerModal
      title={isCorrection ? `Suggest a correction · ${target?.asset || target?.company || ''}` : 'Suggest a catalyst'}
      onClose={onClose}
      footer={!done && (
        <>
          <button type="button" className="ib" data-close onClick={onClose}>Cancel</button>
          <button type="button" className="ib primary" disabled={submitting} onClick={handleSubmit}>
            {submitting ? 'Sending…' : 'Send for verification'}
          </button>
        </>
      )}
    >
      {done ? (
        <p style={{ textAlign: 'center', padding: '24px 0', color: 'var(--pos)', fontWeight: 600 }}>Suggestion submitted — thank you.</p>
      ) : (
        <div className="form">
          <p className="policy">
            Suggestions don&rsquo;t change the calendar. Each one is checked against an independent public source
            (company press release, SEC filing, FDA notice, ClinicalTrials.gov or a congress abstract) and added only
            if it&rsquo;s confirmed. Your own source link is required but isn&rsquo;t accepted on its own.
          </p>

          <label>Company<input value={form.company || ''} list="dl-company" onChange={(e) => update('company', e.target.value)} required /></label>
          <label>Ticker<input value={form.ticker || ''} placeholder="e.g. AZN or Private" onChange={(e) => update('ticker', e.target.value)} /></label>
          <label>Asset (generic name or code, no brand names)
            <input value={form.asset || ''} list="dl-asset" placeholder="e.g. pembrolizumab, vepdegestrant (ARV-471)" onChange={(e) => update('asset', e.target.value)} required />
          </label>
          <label>Generic name<input value={form.generic || ''} onChange={(e) => update('generic', e.target.value)} /></label>
          <label>Target<input value={form.target || ''} placeholder="e.g. BTK, PD-1 x VEGF" onChange={(e) => update('target', e.target.value)} /></label>
          <label>Mechanism class<input value={form.moa || ''} list="dl-moa" onChange={(e) => update('moa', e.target.value)} /></label>
          <label>Modality
            <select value={form.modality || ''} onChange={(e) => update('modality', e.target.value)}>
              <option value="">—</option>
              {MODALITIES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>Phase<input value={form.phase || ''} list="dl-phase" onChange={(e) => update('phase', e.target.value)} /></label>
          <label>Study name<input value={form.trial || ''} placeholder="e.g. TROPION-Lung15" onChange={(e) => update('trial', e.target.value)} /></label>
          <label>NCT<input value={form.nct || ''} placeholder="NCT0…" onChange={(e) => update('nct', e.target.value)} /></label>
          <label>Indication<input value={form.indication || ''} placeholder="line, biomarker, setting" onChange={(e) => update('indication', e.target.value)} /></label>
          <label>Tumor type<input value={form.histology || ''} list="dl-hist" onChange={(e) => update('histology', e.target.value)} /></label>
          <label>Area
            <select value={form.area || ''} onChange={(e) => update('area', e.target.value)}>
              <option value="">—</option>
              <option>Solid</option>
              <option>Heme</option>
            </select>
          </label>
          <label>Catalyst type
            <select value={form.type || ''} onChange={(e) => update('type', e.target.value)}>
              <option value="">—</option>
              {CATALYST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="full">Event<input value={form.event || ''} onChange={(e) => update('event', e.target.value)} required /></label>
          <label>Date (best estimate)<input type="date" value={form.date || ''} onChange={(e) => update('date', e.target.value)} /></label>
          <label>Guided window<input value={form.window || ''} placeholder="e.g. 2H 2026, PDUFA 2026-11-20" onChange={(e) => update('window', e.target.value)} /></label>
          <label>Status
            <select value={form.status || 'upcoming'} onChange={(e) => update('status', e.target.value)}>
              <option value="upcoming">Upcoming</option>
              <option value="past">Happened</option>
            </select>
          </label>
          <label>Outcome
            <select value={form.outcome || ''} onChange={(e) => update('outcome', e.target.value)}>
              <option value="">—</option>
              {OUTCOMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="full">Data / outcome (or what to watch)
            <textarea value={form.data || ''} placeholder="e.g. mPFS 16.6 vs 9.2 mo, HR 0.62 (95% CI 0.51–0.75); ORR 58%" onChange={(e) => update('data', e.target.value)} />
          </label>
          <label>Venue<input value={form.venue || ''} placeholder="ASH 2026" onChange={(e) => update('venue', e.target.value)} /></label>
          <label>Source URL (required)<input type="url" value={form.source || ''} placeholder="https://" onChange={(e) => update('source', e.target.value)} required /></label>

          <fieldset>
            <legend>Delay</legend>
            <label className="inline"><input type="checkbox" checked={delayed} onChange={(e) => setDelayed(e.target.checked)} /> Timing slipped</label>
            <label>Original guidance<input value={form.original_window || ''} onChange={(e) => update('original_window', e.target.value)} /></label>
            <label className="full">Reason / note<input value={form.delay_note || ''} onChange={(e) => update('delay_note', e.target.value)} /></label>
          </fieldset>

          <label className="full">What should change, and why
            <input value={reason} placeholder="e.g. Company moved topline from 1H to 2H 2027 on Q3 call" onChange={(e) => setReason(e.target.value)} />
          </label>
          <p className="note">{error || note}</p>
          <datalist id="dl-company">{companyOptions.map((c) => <option key={c} value={c} />)}</datalist>
          <datalist id="dl-asset">{assetOptions.map((a) => <option key={a} value={a} />)}</datalist>
          <datalist id="dl-moa">{moaOptions.map((m) => <option key={m} value={m} />)}</datalist>
          <datalist id="dl-hist">{histologyOptions.map((h) => <option key={h} value={h} />)}</datalist>
          <datalist id="dl-phase">
            <option value="Preclinical" /><option value="Phase 1" /><option value="Phase 1/2" />
            <option value="Phase 2" /><option value="Phase 3" /><option value="Registrational" /><option value="Marketed" />
          </datalist>
        </div>
      )}
    </TrackerModal>
  );
};

export default SuggestCatalystModal;
